import { useQueries, useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';

import {
  type AchTransfer,
  type CheckTransfer,
  type RealtimeTransfer,
  type SwiftTransfer,
  type TransferReviewBatch,
  TransferReviewBatchDecision,
  type TransferReviewBatchRecord,
  type UnifiedTransfer,
  type WireTransfer,
  approvalsGetTransferReviewBatchOptions,
  transferAchGetTransferOptions,
  transferCheckGetCheckTransferOptions,
  transferRealtimeGetTransferOptions,
  transferSwiftGetTransferOptions,
  transferWireGetWireTransferOptions,
} from '@column/api';
import { Button, Chip, Icon, formatNumber } from '@column/column-ui-kit';
import { Badge, DataTable, type DataTableColumnDef, Skeleton, cn } from '@column/ui';

import { StatusFilterSelect } from '../../_components/StatusFilterSelect';
import { SectionHeader } from '~/components';
import { Empty as TableEmpty, tableDefaults } from '~/components/Table';
import { useNavigate } from '~/lib/navigation';
import { ACHBatchRecordErrorBadge, TransferAmount, TransferTypeBadge } from '~/lib/transfers';
import { getTransferProgressIcon } from '~/lib/transfers/progress';
import { REVIEW_BATCH_POLL_MS, isReviewBatchDone } from '~/lib/transfers/stores/ReviewBatchJobs';
import { TransferProgress } from '~/lib/transfers/types';
import {
  type BadgeTone,
  getTransferBadgeClassName,
  getTransferChipClassName,
  getTransferChipType,
  getTransferURL,
} from '~/lib/transfers/utils';
import { TransferEntryType, convertTransferAmount } from '~/repositories';

import { DIALOG_WIDEN_MS, EASE } from './ReviewDialog';
import { RollingNumber } from './RollingNumber';

const ERROR_PAGE_SIZE_OPTIONS = [5, 10];
const RECORD_TRANSFER_STALE_MS = 60_000;

type RecordTransfer = Pick<UnifiedTransfer, 'id' | 'type' | 'amount' | 'currency_code' | 'is_incoming'> & {
  description?: string | null;
};

const getRecordTransferOptions = (transferId: string) => {
  if (transferId.startsWith('acht_')) {
    return {
      ...transferAchGetTransferOptions({ path: { ach_transfer_id: transferId } }),
      select: (transfer: AchTransfer): RecordTransfer => ({
        ...transfer,
        type: transfer.type === 'DEBIT' ? 'ach_debit' : 'ach_credit',
      }),
    };
  }
  if (transferId.startsWith('wire_')) {
    return {
      ...transferWireGetWireTransferOptions({ path: { wire_transfer_id: transferId } }),
      select: (transfer: WireTransfer): RecordTransfer => ({ ...transfer, type: 'wire' }),
    };
  }
  if (transferId.startsWith('rttr_')) {
    return {
      ...transferRealtimeGetTransferOptions({ path: { realtime_transfer_id: transferId } }),
      select: (transfer: RealtimeTransfer): RecordTransfer => ({ ...transfer, type: 'realtime' }),
    };
  }
  if (transferId.startsWith('swft_')) {
    return {
      ...transferSwiftGetTransferOptions({ path: { swift_transfer_id: transferId } }),
      select: (transfer: SwiftTransfer): RecordTransfer => ({ ...transfer, type: 'swift' }),
    };
  }
  if (transferId.startsWith('chkt_')) {
    return {
      ...transferCheckGetCheckTransferOptions({ path: { check_transfer_id: transferId } }),
      select: (transfer: CheckTransfer): RecordTransfer => ({
        id: transfer.id,
        type: transfer.type === 'CREDIT' ? 'check_credit' : 'check_debit',
        amount: transfer.positive_pay_amount ?? transfer.deposited_amount ?? 0,
        currency_code: transfer.currency_code,
        description: transfer.description,
        is_incoming: transfer.type === 'CREDIT',
      }),
    };
  }
  return undefined;
};

type RecordStatus = TransferReviewBatchRecord['status'];

const RECORD_STATUS_META: Record<RecordStatus, { tone: BadgeTone; label: string; icon: ReactNode }> = {
  pending: { tone: 'secondary', label: 'Pending', icon: getTransferProgressIcon(TransferProgress.PROGRESS_0) },
  approved: { tone: 'success', label: 'Approved', icon: getTransferProgressIcon(TransferProgress.PROGRESS_100) },
  rejected: { tone: 'danger', label: 'Rejected', icon: getTransferProgressIcon(TransferProgress.PROGRESS_FAILED) },
  skipped: { tone: 'secondary', label: 'Skipped', icon: getTransferProgressIcon(TransferProgress.UNKNOWN) },
  failed: { tone: 'danger', label: 'Failed', icon: getTransferProgressIcon(TransferProgress.PROGRESS_FAILED) },
};

const SKIP_REASON_LABELS: Record<string, string> = {
  not_found: 'Not found',
  unsupported: 'Not supported',
  not_held: 'No longer pending approval',
};

interface JobStatus {
  tone: BadgeTone;
  label: string;
  icon: ReactNode;
}

const getJobStatus = (batch: TransferReviewBatch | undefined, error: string | undefined): JobStatus => {
  if (error) {
    return { tone: 'danger', label: 'Not submitted', icon: getTransferProgressIcon(TransferProgress.PROGRESS_FAILED) };
  }
  if (!batch) {
    return { tone: 'info', label: 'Submitting', icon: getTransferProgressIcon(TransferProgress.PROGRESS_25) };
  }
  if (batch.status === 'completed') {
    return { tone: 'success', label: 'Processed', icon: getTransferProgressIcon(TransferProgress.PROGRESS_100) };
  }
  if (batch.status === 'failed') {
    return { tone: 'danger', label: 'Failed', icon: getTransferProgressIcon(TransferProgress.PROGRESS_FAILED) };
  }
  return { tone: 'info', label: 'Processing', icon: getTransferProgressIcon(TransferProgress.PROGRESS_75) };
};

const ATTENTION_STATUSES: RecordStatus[] = ['failed', 'skipped'];

const PROGRESS_TRACK: Record<BadgeTone, string> = {
  info: 'bg-blue-500/20',
  success: 'bg-seafoam-600/20',
  danger: 'bg-red-500/20',
  secondary: 'bg-muted',
};

const PROGRESS_FILL: Record<BadgeTone, string> = {
  info: 'bg-blue-500',
  success: 'bg-seafoam-600',
  danger: 'bg-red-500',
  secondary: 'bg-muted-foreground',
};

interface StateCount {
  key: string;
  label: string;
  count: number;
  tone: BadgeTone;
  icon: ReactNode;
}

const getStateCounts = (
  batch: TransferReviewBatch | undefined,
  decision: TransferReviewBatchDecision
): StateCount[] => [
  {
    key: 'succeeded',
    label: decision === TransferReviewBatchDecision.APPROVED ? 'Approved' : 'Rejected',
    count: batch?.succeeded_count ?? 0,
    tone: 'success',
    icon: getTransferProgressIcon(TransferProgress.PROGRESS_100),
  },
  {
    key: 'skipped',
    label: 'Skipped',
    count: batch?.skipped_count ?? 0,
    tone: 'secondary',
    icon: getTransferProgressIcon(TransferProgress.UNKNOWN),
  },
  {
    key: 'failed',
    label: 'Failed',
    count: batch?.failed_count ?? 0,
    tone: 'danger',
    icon: getTransferProgressIcon(TransferProgress.PROGRESS_FAILED),
  },
];

const getRecordReason = (record: TransferReviewBatchRecord): string | undefined =>
  record.error?.message ??
  (record.skip_reason ? (SKIP_REASON_LABELS[record.skip_reason] ?? record.skip_reason) : undefined);

interface ReviewBatchSummaryProps {
  decision: TransferReviewBatchDecision;
  requestedCount: number;
  reviewBatchId?: string;
  error?: string;
  knownTransfers?: Record<string, UnifiedTransfer>;
  onHasAttentionChange?: (hasAttention: boolean) => void;
}

export const ReviewBatchSummary: React.FC<ReviewBatchSummaryProps> = ({
  decision,
  requestedCount,
  reviewBatchId,
  error,
  knownTransfers,
  onHasAttentionChange,
}) => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(ERROR_PAGE_SIZE_OPTIONS[0]);

  const { data: liveBatch } = useQuery({
    ...approvalsGetTransferReviewBatchOptions({ path: { transfer_review_batch_id: reviewBatchId ?? '' } }),
    enabled: Boolean(reviewBatchId),
    refetchOnMount: 'always',
    refetchInterval: (query) => (isReviewBatchDone(query.state.data) ? false : REVIEW_BATCH_POLL_MS),
  });

  const records = useMemo(() => liveBatch?.records ?? [], [liveBatch]);
  const attentionRecords = useMemo(
    () => records.filter((record) => ATTENTION_STATUSES.includes(record.status)),
    [records]
  );
  const hasAttention = attentionRecords.length > 0;
  useEffect(() => {
    onHasAttentionChange?.(hasAttention);
  }, [hasAttention, onHasAttentionChange]);
  const filteredRecords = useMemo(
    () => (statusFilter ? attentionRecords.filter((record) => record.status === statusFilter) : attentionRecords),
    [attentionRecords, statusFilter]
  );
  const pageRecords = filteredRecords.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

  const missing = pageRecords.flatMap(({ transfer_id: transferId }) => {
    const options = knownTransfers?.[transferId] ? undefined : getRecordTransferOptions(transferId);
    return options ? [{ transferId, options }] : [];
  });
  const missingIds = missing.map(({ transferId }) => transferId);
  const lookups = useQueries({
    queries: missing.map(({ options }) => ({ ...options, staleTime: RECORD_TRANSFER_STALE_MS })),
  });
  const lookedUp = new Map(
    missingIds.map((transferId, index) => [
      transferId,
      { transfer: lookups[index]?.data as RecordTransfer | undefined, isLoading: Boolean(lookups[index]?.isLoading) },
    ])
  );
  const getTransfer = (record: TransferReviewBatchRecord): RecordTransfer | undefined =>
    knownTransfers?.[record.transfer_id] ?? lookedUp.get(record.transfer_id)?.transfer;

  const statusOptions = useMemo(
    () => [
      { label: 'All', value: '' },
      ...ATTENTION_STATUSES.filter((status) => attentionRecords.some((record) => record.status === status)).map(
        (status) => ({ label: RECORD_STATUS_META[status].label, value: status })
      ),
    ],
    [attentionRecords]
  );

  const hasReasons = pageRecords.some((record) => getRecordReason(record));
  const columns: DataTableColumnDef<TransferReviewBatchRecord>[] = [
    {
      header: 'Amount / Description',
      accessor: 'transfer_id',
      width: 'minmax(200px, auto)',
      className: 'min-w-0',
      cell: ({ row, value }) => {
        const transfer = getTransfer(row);
        if (!transfer) {
          return lookedUp.get(value)?.isLoading ? (
            <Skeleton className="h-4 w-40" />
          ) : (
            <span className="truncate font-mono text-xs text-muted-foreground">{value}</span>
          );
        }
        return (
          <TransferAmount
            amount={Math.abs(transfer.amount ?? 0)}
            currencyCode={transfer.currency_code}
            description={transfer.description ?? undefined}
            isAdding={convertTransferAmount(transfer.type as TransferEntryType, transfer.is_incoming) > 0}
          />
        );
      },
    },
    {
      id: 'type',
      header: 'Type',
      width: 'minmax(min-content, 150px)',
      cell: ({ row }) => {
        const transfer = getTransfer(row);
        return transfer ? <TransferTypeBadge type={transfer.type as TransferEntryType} /> : null;
      },
    },
    {
      header: 'Status',
      accessor: 'status',
      width: 'minmax(min-content, 150px)',
      cell: ({ row }) => {
        const meta = RECORD_STATUS_META[row.status];
        return (
          <Badge variant="ghost" className={getTransferBadgeClassName(meta.tone)}>
            {meta.icon}
            {meta.label}
          </Badge>
        );
      },
    },
    ...(hasReasons
      ? [
          {
            id: 'reason',
            header: 'Reason',
            width: 'min-content',
            cell: ({ row }: { row: TransferReviewBatchRecord }) =>
              row.error ? (
                <ACHBatchRecordErrorBadge error={row.error} />
              ) : row.skip_reason ? (
                <Badge variant="ghost" className={getTransferBadgeClassName('secondary')}>
                  {getRecordReason(row)}
                </Badge>
              ) : null,
          },
        ]
      : []),
  ];

  const jobStatus = getJobStatus(liveBatch, error);
  const stateCounts = getStateCounts(liveBatch, decision);
  const totalCount = liveBatch?.total_count ?? requestedCount;
  const processedCount = liveBatch ? totalCount - liveBatch.pending_count : 0;

  return (
    <div className="-mx-6 flex min-w-0 flex-col [--page-margin:1.5rem]">
      <div className={cn('px-6', hasAttention && 'pb-5')}>
        <div className="flex items-center justify-between gap-4">
          <Chip
            className={cn('[&>svg]:block', getTransferChipClassName(jobStatus.tone))}
            variant="subtle"
            size="large"
            type={getTransferChipType(jobStatus.tone)}
            icon={jobStatus.icon}
          >
            <span className="flex h-5 items-center">{jobStatus.label}</span>
          </Chip>
          <span className="flex h-5 items-center gap-1 whitespace-nowrap text-sm font-medium leading-5 text-muted-foreground">
            <RollingNumber value={processedCount}>{(value) => formatNumber(value, false)}</RollingNumber>
            of {formatNumber(totalCount, false)} processed
          </span>
        </div>
        <div
          className={cn(
            'mt-3 flex h-2 w-full overflow-hidden rounded-full transition-colors',
            PROGRESS_TRACK[jobStatus.tone]
          )}
        >
          {stateCounts.map((state) => (
            <div
              key={state.key}
              className={cn('h-full transition-[width] duration-500 ease-out', PROGRESS_FILL[state.tone])}
              style={{ width: `${totalCount > 0 ? (state.count / totalCount) * 100 : 0}%` }}
            />
          ))}
        </div>
        <ul className="mt-3 flex min-h-5 flex-wrap items-center gap-x-4 gap-y-2">
          <AnimatePresence initial={false} mode="popLayout">
            {stateCounts
              .filter((state) => state.count > 0)
              .map((state) => (
                <motion.li
                  key={state.key}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.2, ease: EASE }}
                >
                  <Chip
                    className={cn('[&>svg]:block', getTransferChipClassName(state.tone))}
                    variant="subtle"
                    size="large"
                    type={getTransferChipType(state.tone)}
                    icon={state.icon}
                  >
                    <span className="flex h-5 items-center gap-1">
                      <RollingNumber value={state.count}>{(value) => formatNumber(value, false)}</RollingNumber>
                      {state.label}
                    </span>
                  </Chip>
                </motion.li>
              ))}
          </AnimatePresence>
        </ul>
        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
      </div>

      {hasAttention && (
        <motion.div
          className="flex min-w-0 flex-col"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2, delay: DIALOG_WIDEN_MS / 1000 }}
        >
          <SectionHeader text="Errors">
            <StatusFilterSelect
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                setPageIndex(0);
              }}
              options={statusOptions}
            />
          </SectionHeader>
          <div className="px-6">
            <DataTable<TransferReviewBatchRecord>
              {...tableDefaults}
              columns={columns}
              data={pageRecords}
              isLoading={!liveBatch && !error}
              rowKey={(row) => row.transfer_id}
              pagination={
                filteredRecords.length <= ERROR_PAGE_SIZE_OPTIONS[0]
                  ? undefined
                  : {
                      pageIndex,
                      pageSize,
                      pageSizeOptions: ERROR_PAGE_SIZE_OPTIONS,
                      hasPrevious: pageIndex > 0,
                      hasNext: (pageIndex + 1) * pageSize < filteredRecords.length,
                      onPrevious: () => setPageIndex((index) => Math.max(index - 1, 0)),
                      onNext: () => setPageIndex((index) => index + 1),
                      onPageSizeChange: (value) => {
                        setPageSize(value);
                        setPageIndex(0);
                      },
                    }
              }
              rowAction={(row) => {
                const transfer = getTransfer(row);
                return transfer ? (
                  <Button
                    onClick={(event: React.SyntheticEvent) => {
                      event.stopPropagation();
                      navigate(getTransferURL(transfer.id, transfer.type as TransferEntryType));
                    }}
                    icon={<Icon.ChevronRightLarge />}
                    iconRight
                    size="tiny"
                    variant="secondary"
                  >
                    View
                  </Button>
                ) : null;
              }}
              emptyContent={
                <TableEmpty
                  icon={<Icon.AnimationArrows />}
                  title="No entries found"
                  description={statusFilter ? 'No entries were found matching the specified filters' : undefined}
                />
              }
            />
          </div>
        </motion.div>
      )}
    </div>
  );
};
